import Area from 'models/Area';
import Category from 'models/Category';
import PoiType from 'models/PoiType';
import { ClientLocation } from 'contexts/AppContext';

export const multipleParamsSeparator = '|';

export interface SearchFilter {
    options?: PoiType[];
    slug: string;
    name?: string;
    value: string;
}

export interface SearchSort {
    value: string;
    direction?: string;
}

export interface AvailableSearchSort {
    name: string;
    slug: string;
}

interface UrlQueryParams {
    currentPage?: number;
    filters?: SearchFilter[];
    mapView?: boolean;
    sort?: SearchSort;
    sortDirection?: string;
    perPage?: number;
}

interface UrlParams extends UrlQueryParams {
    area?: Area;
    category?: Category;
}

export const generateUrl = ({ currentPage, area, category, sort, sortDirection, filters, mapView }: UrlParams) => {
    const areaSlug = area && area.slug !== 'any' ? area.slug : null;
    const categorySlug = category && category.slug !== 'any' ? category.slug : null;
    const baseUrl = buildBaseUrl(areaSlug, categorySlug);
    const query = buildUrlQuery({
        currentPage,
        filters,
        mapView,
        sort,
        sortDirection
    });

    return query ? `${baseUrl}?${query}` : baseUrl;
};

export const buildUrlQuery = ({
    currentPage,
    filters = [],
    mapView = false,
    sort,
    sortDirection,
    perPage
}: UrlQueryParams) => {
    const params = {};

    if (currentPage && currentPage !== 1) params['page'] = currentPage;
    if (filters?.length > 0)
        params['filters'] = filters.map((f) => `${f.slug}:${f.value}`).join(multipleParamsSeparator);

    if (mapView) params['map'] = true;
    if (sort?.value) params['sort'] = sort.value;
    if (sortDirection === 'DESC') params['sortDirection'] = sortDirection;
    if (perPage) params['perPage'] = perPage;

    return new URLSearchParams(params).toString();
};

interface SearchParamsQuery {
    area?: Area;
    category?: Category;
}

export const buildParamsQuery = ({ area, category }: SearchParamsQuery) => {
    if (!area && !category) return '';
    const params = {};
    params['area'] = area?.slug;
    params['category'] = category?.slug;
    return new URLSearchParams(params).toString();
};

interface ApiQueryParams extends UrlParams {
    sort?: SearchSort;
    clientLocation?: ClientLocation;
}

export const buildApiQuery = ({
    area,
    category,
    currentPage = 1,
    filters,
    sort,
    sortDirection,
    perPage = 30,
    mapView,
    clientLocation
}: ApiQueryParams) => {
    let postFilters = {};

    if (category?.slug && category?.slug.trim() !== 'any') {
        postFilters['filter[categories.slug]'] = category.slug;
    }

    if (area?.slug && area?.slug.trim() !== 'any') {
        postFilters['filter[areas.slug]'] = area.slug;
    }

    if (filters) {
        const urlFilters = filters
            .map((f) => `${f.slug}:${f.value}`)
            .join(multipleParamsSeparator)
            .split(multipleParamsSeparator);
        const parsedFilters: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            [key: string]: any;
        } = { 'filter[attributes]': [], 'filter[type.slug]': [] };
        for (let i = 0; i < urlFilters.length; i++) {
            const urlFilter = urlFilters[i];
            const { 0: filter, 1: value } = urlFilter.split(':');
            if (filter === 'poi-type') {
                parsedFilters['filter[type.slug]'].push(value);
            } else {
                parsedFilters['filter[attributes]'].push(urlFilter);
            }
        }
        parsedFilters['filter[attributes]'] = parsedFilters['filter[attributes]'].join(',');
        parsedFilters['filter[type.slug]'] = parsedFilters['filter[type.slug]'].join(',');
        postFilters = { ...postFilters, ...parsedFilters };
    }

    const postParams = {
        ...postFilters,
        'page[size]': perPage.toString(),
        'page[number]': currentPage.toString()
    };

    if (sort?.value) {
        if (sort.value === 'nearby') {
            if (clientLocation?.latitude && clientLocation?.longitude) {
                postParams['sort'] = `nearby`;
                postParams['lat'] = clientLocation.latitude;
                postParams['lng'] = clientLocation.longitude;
            }
        } else {
            postParams['sort'] = `${sortDirection === 'DESC' ? '-' : ''}${sort.value}`;
        }
    }

    if (mapView) {
        postParams['mapView'] = true;
    }

    return new URLSearchParams(postParams).toString();
};

const buildBaseUrl = (areaSlug, categorySlug) => {
    if (categorySlug && areaSlug) {
        return `/ideje/${areaSlug}/${categorySlug}`;
    }
    if (areaSlug) {
        return `/ideje/${areaSlug}`;
    }
    if (categorySlug) {
        return `/ideje/${categorySlug}`;
    }
    return '/ideje';
};

const parseUrlFilters = (query) => {
    const filters = query.split(multipleParamsSeparator);
    return filters.map((filter) => ({
        slug: filter.split(':')[0],
        value: filter.split(':')[1]
    }));
};

interface ParseUrlQueryParams {
    filters?: string;
    sort?: string;
    page?: string;
    map?: string;
    sortDirection?: string;
}

interface ParsedUrlQueryResult {
    filters: SearchFilter[];
    sort: SearchSort;
    currentPage: number;
    mapView: boolean;
    sortDirection: 'DESC' | 'ASC';
}

export const parseUrlQuery = ({
    filters,
    sort,
    page,
    map,
    sortDirection
}: ParseUrlQueryParams): ParsedUrlQueryResult => {
    return {
        filters: filters ? parseUrlFilters(filters) : [],
        sort: sort ? { value: sort } : null,
        currentPage: page ? parseInt(page) : 1,
        mapView: !!map,
        sortDirection: sortDirection ? 'DESC' : 'ASC'
    };
};
